// UI slice for managing UI state (sidebar, modals, etc.)

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { storageService } from '@/services'

interface ModalState {
  isOpen: boolean
  type: string | null
  data: any
}

interface UIState {
  sidebarCollapsed: boolean
  modal: ModalState
  loading: {
    [key: string]: boolean
  }
}

const initialState: UIState = {
  sidebarCollapsed: storageService.getSidebarCollapsed(),
  modal: {
    isOpen: false,
    type: null,
    data: null,
  },
  loading: {},
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Sidebar
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
      storageService.setSidebarCollapsed(state.sidebarCollapsed)
    },

    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload
      storageService.setSidebarCollapsed(action.payload)
    },

    // Modal
    openModal: (state, action: PayloadAction<{ type: string; data?: any }>) => {
      state.modal.isOpen = true
      state.modal.type = action.payload.type
      state.modal.data = action.payload.data || null
    },

    closeModal: (state) => {
      state.modal.isOpen = false
      state.modal.type = null
      state.modal.data = null
    },

    setModalData: (state, action: PayloadAction<any>) => {
      state.modal.data = action.payload
    },

    // Loading states
    setLoading: (state, action: PayloadAction<{ key: string; value: boolean }>) => {
      state.loading[action.payload.key] = action.payload.value
    },

    clearLoading: (state) => {
      state.loading = {}
    },
  },
})

export const {
  toggleSidebar,
  setSidebarCollapsed,
  openModal,
  closeModal,
  setModalData,
  setLoading,
  clearLoading,
} = uiSlice.actions

export default uiSlice.reducer

// Selectors
export const selectSidebarCollapsed = (state: { ui: UIState }) => state.ui.sidebarCollapsed
export const selectModal = (state: { ui: UIState }) => state.ui.modal
export const selectLoading = (key: string) => (state: { ui: UIState }) => state.ui.loading[key] || false
